import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { useCachedResource } from "../hooks/useCachedResource";
import { toast } from "sonner";
import { createBox, listBoxes, listRecons } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { CashboxCard } from "../features/acct/CashboxCard";
import { ReconForm } from "../features/acct/ReconForm";
import { Money } from "../features/acct/Money";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../components/neu/Neu";

export default function AcctCashPage() {
  const { t } = useTranslation();
  const [pickedId, setPickedId] = useState(null);
  const [form, setForm] = useState({ name: "", kind: "cash", opening_balance: "" });
  const [adding, setAdding] = useState(false);

  const { data, reload: load } = useCachedResource("acct-cash", async () => ({
    boxes: await listBoxes(),
    recons: await listRecons({}),
  }));
  const boxes = data?.boxes || [];
  const recons = data?.recons || [];
  const picked = boxes.find((b) => b.id === pickedId) || null;

  const save = async () => {
    await createBox({ ...form, opening_balance: Number(form.opening_balance || 0) });
    toast.success(t("acct.boxCreated"));
    setAdding(false);
    setForm({ name: "", kind: "cash", opening_balance: "" });
    load();
  };

  return (
    <AcctShell
      titleKey="cash"
      actions={
        <NeuButton variant="primary" className="flex items-center gap-2" data-testid="box-add-btn" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" /> {t("acct.newBox")}
        </NeuButton>
      }
    >
      {adding ? (
        <NeuCard className="grid gap-3 p-5 sm:grid-cols-4" data-testid="box-form">
          <NeuInput data-testid="box-name" placeholder={t("acct.boxName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <NeuSelect data-testid="box-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="cash">{t("acct.boxKind.cash")}</option>
            <option value="bank">{t("acct.boxKind.bank")}</option>
          </NeuSelect>
          <NeuInput data-testid="box-opening" type="number" placeholder={t("acct.openingBalance")} value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
          <NeuButton variant="primary" data-testid="box-save" disabled={!form.name} onClick={save}>
            {t("app.save")}
          </NeuButton>
        </NeuCard>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="cashbox-list">
        {boxes.map((box) => (
          <CashboxCard key={box.id} box={box} active={picked?.id === box.id} onPick={(b) => setPickedId(b.id)} />
        ))}
      </div>

      {picked ? <ReconForm box={picked} onDone={load} /> : null}

      <NeuCard className="p-4" data-testid="recon-history">
        <p className="label-xs mb-3">{t("acct.reconHistory")}</p>
        {recons.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 py-1 text-xs" data-testid={`recon-row-${r.id}`}>
            <span className="font-mono" dir="ltr">{r.date}</span>
            <Money value={r.system_balance} currency={r.currency} />
            <span className="text-muted-foreground">/</span>
            <Money value={r.counted} currency={r.currency} dim />
            <span className="text-muted-foreground">Δ</span>
            <Money value={r.difference} currency={r.currency} className="font-bold" />
            {r.entry_number ? <span className="font-mono text-primary" dir="ltr">· {r.entry_number}</span> : null}
          </div>
        ))}
      </NeuCard>
    </AcctShell>
  );
}
